import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { MonthYearSelect } from "@lending/components/MonthYearSelect/MonthYearSelect";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { Select } from "@lending/components/Select/Select";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useCreateApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useCreateApplicantIncomeDetailsQuery";
import { useListApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useListApplicantIncomeDetailsQuery";
import { usePutApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/usePutApplicantIncomeDetailsQuery";
import { useGetCoApplicantQuery } from "@lending/hooks/queries/useGetCoApplicantQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { EMPLOYMENT_TYPES, IncomeTypeEnum } from "@lending/types";
import { useStore } from "../../../../contexts/StoreContext";
import {
  createEmploymentDetailsSchema,
  formatEmploymentStartDate,
  getEmploymentFieldResetPayload,
} from "../../employmentDetailsForm";

interface FormData {
  income_type: IncomeTypeEnum | undefined;
  job_title: string;
  employer_name: string;
  employment_start_month: string;
  employment_start_year: string;
}

function CoApplicantIncome() {
  const { l10n } = useLocalization();
  const { goBack, goNext, startOver } = useFlowNavigation();
  const { showGenericErrorToast } = useToastsContext();
  const { updateData } = useStore();

  const { coApplicant, isLoading: isApplicationLoading } = useGetCoApplicantQuery();

  const { data: incomeDetailsResponse, isLoading: isIncomeDetailsLoading } =
    useListApplicantIncomeDetailsQuery();
  const incomeDetails =
    incomeDetailsResponse?.results?.find(
      (income) => income.lending_application_applicant_details === coApplicant?.id
    ) ?? null;
  const lastInitializedFormKey = useRef<string | null>(null);
  const schema = createEmploymentDetailsSchema(l10n);

  const { handleSubmit, control, reset, watch } = useForm<FormData>({
    defaultValues: {
      income_type: undefined,
      job_title: "",
      employer_name: "",
      employment_start_month: "",
      employment_start_year: "",
    },
    resolver: zodResolver(schema),
  });

  const selectedIncomeType = watch("income_type") as IncomeTypeEnum | undefined;
  const isOtherSelected = selectedIncomeType === "other";
  const isRetiredSelected = selectedIncomeType === "retirement";

  useEffect(() => {
    if (!incomeDetails) {
      return;
    }

    const initializationKey = [
      incomeDetails.id,
      incomeDetails.income_type ?? "",
      incomeDetails.job_title ?? "",
      incomeDetails.employer_name ?? "",
      incomeDetails.employment_start_month ?? "",
      incomeDetails.employment_start_year ?? "",
    ].join("|");

    if (lastInitializedFormKey.current === initializationKey) {
      return;
    }

    lastInitializedFormKey.current = initializationKey;

    reset({
      income_type: incomeDetails.income_type as IncomeTypeEnum,
      job_title: incomeDetails.job_title ?? "",
      employer_name: incomeDetails.employer_name ?? "",
      ...formatEmploymentStartDate(
        incomeDetails.employment_start_month,
        incomeDetails.employment_start_year
      ),
    });
  }, [incomeDetails, reset]);

  // Both saves are awaited (we read the created record and then navigate), so onSubmit
  // owns the failure in its catch rather than splitting it into an onError callback.
  const { mutateAsync: updateApplicantIncome, isPending: isUpdating } =
    usePutApplicantIncomeDetailsQuery();

  const { mutateAsync: createApplicantIncome, isPending: isCreating } =
    useCreateApplicantIncomeDetailsQuery();

  async function onSubmit(data: FormData) {
    const incomeType = data.income_type!;

    if (!coApplicant) {
      showGenericErrorToast();
      startOver();
      return;
    }

    const body = {
      income_type: incomeType,
      amount: incomeDetails?.amount ?? 0,
      job_title: incomeType === "retirement" ? null : data.job_title,
      employer_name:
        incomeType === "retirement" || incomeType === "other" ? null : data.employer_name,
      employment_start_month:
        incomeType === "retirement" || incomeType === "other"
          ? null
          : Number(data.employment_start_month),
      employment_start_year:
        incomeType === "retirement" || incomeType === "other"
          ? null
          : Number(data.employment_start_year),
      ...getEmploymentFieldResetPayload(incomeType),
    };

    try {
      if (incomeDetails?.id) {
        const updatedIncomeDetails = await updateApplicantIncome({
          params: { path: { uuid: incomeDetails.id } },
          // @ts-expect-error openapi-typescript does not respect readonly payload fields
          body,
        });
        updateData({
          coApplicantIncomeDetailsId: updatedIncomeDetails?.id ?? incomeDetails.id,
          coApplicantIncomeType: incomeType,
        });
      } else {
        const createdIncomeDetails = await createApplicantIncome({
          // @ts-expect-error openapi-typescript does not respect readonly payload fields
          body: {
            lending_application_applicant_details: coApplicant.id,
            ...body,
          },
        });
        updateData({
          coApplicantIncomeDetailsId: createdIncomeDetails?.id,
          coApplicantIncomeType: incomeType,
        });
      }

      goNext({ coApplicantIncomeType: incomeType });
    } catch (error) {
      // Stay on the page; the user can retry.
      Sentry.captureException(error);
      showGenericErrorToast();
    }
  }

  const coApplicantName = coApplicant?.first_name ?? "";
  const isLoading = isCreating || isUpdating;

  return (
    <PageLayout isLoading={isApplicationLoading || isIncomeDetailsLoading}>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "co-applicant-employment-details-heading",
            { name: coApplicantName },
            `Tell us about ${coApplicantName}'s employment`
          )}
          subheading={l10n.getString(
            "employment-details-subheading",
            null,
            "This information is used for identity and income verification only. We will not contact your employer without your permission."
          )}
        />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="margin--bottom--l">
            <Select
              name="income_type"
              label={l10n.getString("add-income-employment-type", null, "Employment type")}
              control={control}
              items={EMPLOYMENT_TYPES}
            />
          </div>

          {!isRetiredSelected && selectedIncomeType && (
            <div className="margin--bottom--xxl">
              <div className="margin--bottom--l">
                <TextInput
                  name="job_title"
                  label={
                    isOtherSelected
                      ? l10n.getString("employment-details-job-description", null, "Description")
                      : l10n.getString("employment-details-job-title", null, "Job title")
                  }
                  control={control}
                />
              </div>

              {!isOtherSelected && (
                <>
                  <div className="margin--bottom--l">
                    <TextInput
                      name="employer_name"
                      label={l10n.getString("employment-details-company", null, "Company")}
                      control={control}
                    />
                  </div>
                  <MonthYearSelect
                    control={control}
                    monthName="employment_start_month"
                    yearName="employment_start_year"
                    maxDate={{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }}
                  />
                </>
              )}
            </div>
          )}

          <div>
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={goBack} />
              <Button
                label={l10n.getString("add-income-submit", null, "Next")}
                type="submit"
                isLoading={isLoading}
              />
            </Row>
          </div>
        </form>
      </PageContent>
    </PageLayout>
  );
}

export { CoApplicantIncome };
